import { InjectBaseModalProps } from "@/hooks/Modal/ModalStore";
import { move } from "@/utils/Array";
import { InfoCircleFilled } from "@ant-design/icons";
import { Button, Checkbox, message, Modal } from "antd";
import { xor } from "lodash";
import { observer } from "mobx-react-lite";
import React, { useEffect, useState } from "react";
import SearchFieldDragItem, { SearchFieldDragAction } from "./SearchFieldDragItem";
import AutoLoadingButton from "@/components/AutoLoadingButton";
import { useOrderManagementStore } from "@/scenes/OrderManagementForAllShop/stores/OrderManagementForAllShopStore";
import {
    allFrequentlyUsedFields,
    defaultFrequentlyUsedFields,
} from "@/scenes/OrderManagement/store/OrderTableStorageStore/utils/orderSearchPanel";
import { collectEvent } from "@/services/dataTrack";
import DataTrackEvent from "@/services/dataTrack/enum/DataTrackEvent";
import FrequentlyUsedFieldNameMap from "@/map/FrequentlyUsedFieldNameMap";
import "./index.less";

interface OrderSearchPaneSettingModalProps extends InjectBaseModalProps {}

const OrderSearchPaneSettingModal: React.FC<OrderSearchPaneSettingModalProps> = function (props) {
    const { open, onCancel } = props;

    const { storage } = useOrderManagementStore();

    const {
        /** 实际选中的值 */
        frequentlyUsedFields,
        unFrequentlyUsedFields,
        saveOrderSearchPanelSettingList,
    } = storage;

    /** 所有的值(显示在面板中的) */
    const [panelFrequentlyUsedFields, updatePanelFrequentlyUsedFields] = useState([
        ...frequentlyUsedFields,
        ...unFrequentlyUsedFields,
    ]);

    /** 未更新到store的值，用于受控checkbox */
    const [checkedFrequentlyUsedFields, updateCheckedFrequentlyUsedFields] = useState(frequentlyUsedFields);

    function handleChange(action: SearchFieldDragAction, originIndex: number, targetIndex: number) {
        const cloneFrequentlyUsedFields = [...panelFrequentlyUsedFields];

        const originSearchField = panelFrequentlyUsedFields[originIndex];

        const targetSearchField = panelFrequentlyUsedFields[targetIndex];

        if (action === SearchFieldDragAction.REPLACE) {
            cloneFrequentlyUsedFields[originIndex] = targetSearchField;

            cloneFrequentlyUsedFields[targetIndex] = originSearchField;
        } else if (action === SearchFieldDragAction.INSERT_AFTER) {
            const moveIndex = Math.min(targetIndex + 1, allFrequentlyUsedFields.length - 1);

            move(cloneFrequentlyUsedFields, originIndex, moveIndex);
        }

        updatePanelFrequentlyUsedFields(cloneFrequentlyUsedFields);
    }

    function buildItem() {
        /** 5个为一组Row */
        const rowElementList: React.ReactNode[][] = [];

        let needCreateNext = false;

        panelFrequentlyUsedFields.forEach((frequentlyUsedField, index) => {
            let colElements: React.ReactNode[];

            if (index === 0 || needCreateNext) {
                rowElementList.push([]);
            }

            needCreateNext = false;

            colElements = rowElementList[rowElementList.length - 1];

            const draggable = checkedFrequentlyUsedFields.includes(frequentlyUsedField);

            colElements.push(
                <SearchFieldDragItem
                    frequentlyUsedField={frequentlyUsedField}
                    key={frequentlyUsedField}
                    index={index}
                    onChange={handleChange}
                    draggable={draggable}
                />,
            );

            if (colElements.length === 5) {
                needCreateNext = true;
            }
        });

        return rowElementList.map((colElementList, index) => {
            return (
                <div className="order-search-pane-setting-row" key={index}>
                    {colElementList}
                </div>
            );
        });
    }

    function handleReset() {
        updateCheckedFrequentlyUsedFields(defaultFrequentlyUsedFields);

        /** 恢复到原始的排序顺序 */
        updatePanelFrequentlyUsedFields([
            ...defaultFrequentlyUsedFields,
            ...xor(allFrequentlyUsedFields, defaultFrequentlyUsedFields),
        ]);
    }

    async function handleSave() {
        if (!checkedFrequentlyUsedFields?.length) {
            Modal.error({ title: "请至少选择一个选项" });

            return;
        }

        const sortedFrequentlyUsedFields = [];

        panelFrequentlyUsedFields.forEach((field) => {
            if (checkedFrequentlyUsedFields.includes(field)) {
                sortedFrequentlyUsedFields.push(field);
            }
        });

        await saveOrderSearchPanelSettingList(sortedFrequentlyUsedFields);

        message.success("保存成功");

        onCancel();

        try {
            const content = sortedFrequentlyUsedFields.map((field) => FrequentlyUsedFieldNameMap[field]).join();
            collectEvent(DataTrackEvent.EDIT_SEARCH_PANE, { content });
        } catch {}
    }

    useEffect(() => {
        updateCheckedFrequentlyUsedFields(frequentlyUsedFields);
    }, [frequentlyUsedFields]);

    return (
        <Modal
            className="order-search-pane-setting-modal"
            open={open}
            onCancel={onCancel}
            title="全部选项编辑排序"
            width={1000}
            footer={
                <>
                    <Button className="mr2" onClick={onCancel}>
                        取消
                    </Button>
                    <Button className="mr2" onClick={handleReset}>
                        重置
                    </Button>
                    <AutoLoadingButton className="mr2" type="primary" onClick={handleSave}>
                        保存
                    </AutoLoadingButton>
                </>
            }
        >
            <span className="order-search-pane-setting-tip mb10">
                <InfoCircleFilled className="warn mr5" />

                <span className="error">
                    勾选中的搜索项为常用搜索项，并且支持拖动排序。常用搜索项会默认展示，最少勾选1个
                </span>
            </span>

            <Checkbox.Group
                className="order-search-pane-setting-list w"
                value={checkedFrequentlyUsedFields}
                /* @ts-ignore */
                onChange={updateCheckedFrequentlyUsedFields}
            >
                {buildItem()}
            </Checkbox.Group>
        </Modal>
    );
};

export default observer(OrderSearchPaneSettingModal);
